import React from "react";
import ReactDOM from "react-dom/client";
// @oxbo/tokens injects the three-layer theming CSS on import (must be first).
import "@oxbo/tokens";
import { Providers } from "@app/providers";
import { AppRouter } from "@app/router";
import { startMocks } from "./mocks/browser";
import { initDevAuth } from "@api/devAuth";
import { initProdAuth, getProdOrgRoles, isProdAuthEnabled } from "@api/prodAuth";
import {
  initBrokerAuth,
  isBrokerAuthEnabled,
  isBrokerAuthenticated,
  getBrokerRoles,
} from "@api/brokerAuth";
import { store } from "@app/store";
import { setRole } from "@app/slices/prefsSlice";

/** Roles (from the token) that grant the data-admin / super_admin UI. */
function grantsAdmin(roles: string[]): boolean {
  return roles.some((r) => ["owner", "data_admin", "super_admin", "admin"].includes(r));
}

async function bootstrap() {
  // Standalone mode: MSW serves synthetic M/P data so the app runs with no backend.
  await startMocks();

  // Real-backend dev mode: mint the first Oxbo-M JWT before render and arm the
  // ~15-min refresh loop. No-op unless VITE_DEV_AUTH=true.
  await initDevAuth();

  // Real-login prod mode: log the demo user into the prod IdP before render and
  // arm the refresh loop. No-op unless VITE_PROD_AUTH=true.
  await initProdAuth();

  // RCI-Auth broker prod mode (the Olympic install): run the OIDC auth-code +
  // PKCE flow. On the `/auth/callback` route this is a no-op (the callback
  // screen does the exchange itself); otherwise, if unauthenticated, it either
  // silently refreshes a surviving session or REDIRECTS to the broker — in which
  // case control never returns here and nothing below renders. No-op unless
  // VITE_BROKER_AUTH=true.
  await initBrokerAuth();
  if (isBrokerAuthEnabled() && !isBrokerAuthenticated()) {
    // We are on the callback route (which renders AuthCallback) or a redirect is
    // in flight. Let the router render the callback screen; skip the eager role
    // wiring, which happens after the exchange lands us back in the app.
    renderApp();
    return;
  }

  // Surface the user's admin capability so the (admin-only) data-lineage overlay
  // is reachable. Prefer the broker token's role claims; fall back to the prod
  // IdP `org_roles`. `owner`/`super_admin`/`data_admin`/`admin` all map to the
  // data_admin UI role.
  if (isBrokerAuthEnabled()) {
    if (grantsAdmin(getBrokerRoles())) store.dispatch(setRole("data_admin"));
  } else if (isProdAuthEnabled()) {
    const roles = getProdOrgRoles();
    if (roles.includes("data_admin") || roles.includes("owner")) {
      store.dispatch(setRole("data_admin"));
    }
  }

  renderApp();
}

function renderApp() {
  const rootEl = document.getElementById("root");
  if (!rootEl) throw new Error("Failed to find the root element");

  ReactDOM.createRoot(rootEl).render(
    <React.StrictMode>
      <Providers>
        <AppRouter />
      </Providers>
    </React.StrictMode>,
  );
}

void bootstrap();
